import Vue from 'vue'

// Multi-select / cart mode state. When `isActive` is true, the calendar
// surfaces a "create cart" mode: clicking an open slot opens BookingDrawer
// in a non-modal cart layout so staff can keep clicking slots on the
// calendar to add them to a single transaction in the drawer.
//
// Stage A scope: just the toggle. The cart contents (slots, tickets, etc.)
// will live alongside `isActive` in later stages; for now BookingDrawer's
// existing single-slot prop wiring is enough to verify the non-modal layout.
//
// Why a global observable instead of a Vuex store: matches `appendMode.js`
// — the existing pattern for cross-surface UI state that several disjoint
// parents (Calendar / Bookings / Kanban / ResourceCalendar)
// need to read without prop drilling through 5 wrappers.

const STORAGE_KEY = 'otc.multiSelect'

function load() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

function persist(state) {
  try {
    if (state.isActive || (state.cartSlots && state.cartSlots.length > 0)) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        isActive: state.isActive,
        cartSlots: state.cartSlots
      }))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch (e) {
    // sessionStorage disabled (private mode, etc.) — non-fatal
  }
}

const initial = load() || {}

// cartSlots holds the slot entries the user has added to the in-progress
// transaction. Each entry stores the slot id PLUS the display fields
// (game name, date, time, is_public_booking) captured from the calendar
// card at click time, so CartLine can render the slot header without an
// extra fetch. Pricing is fetched per-CartLine via
// companies-get-pricing-for-slot at mount.
//
// Stage A.1 stored bare ids; B.2 promoted to objects when CartLine
// extraction made the per-slot header data necessary. Legacy sessionStorage
// entries from Stage A.1 (bare numbers) are migrated transparently below.
export const multiSelectState = Vue.observable({
  isActive: !!initial.isActive,
  cartSlots: Array.isArray(initial.cartSlots)
    ? initial.cartSlots
        .map(s => (typeof s === 'object' && s !== null ? s : { id: s }))
        .filter(s => s.id != null)
    : []
})

export function enterMultiSelect() {
  multiSelectState.isActive = true
  persist(multiSelectState)
}

// Exits cart mode AND clears the cart. Toolbar toggle path: clicking the
// active button drops everything in progress. Stage C's submit flow will
// also call this to clear the cart after a successful new transaction.
export function exitMultiSelect() {
  multiSelectState.isActive = false
  multiSelectState.cartSlots = []
  persist(multiSelectState)
}

// Add a slot to the cart (idempotent — clicking the same slot twice
// shouldn't double-count). Captures display fields off the calendar
// card so each CartLine can render its slot header without a separate
// fetch. Returns true if newly added, false if it was already in the cart.
export function addSlotToCart(slot) {
  if (!slot) return false
  const row = slot.originalData || slot
  const id = row.id
  if (id == null) return false
  if (multiSelectState.cartSlots.some(s => s.id === id)) return false

  const entry = {
    id,
    game_id: row.game_id || row.gameId || null,
    game_name: row.game_name || row.gameName || 'Booking',
    game_image: row.game_image || row.gameImage || row.img || null,
    booking_date: row.booking_date || row.bookingDate || null,
    end_date: row.end_date || row.endDate || null,
    start_time: row.start_time || row.startTimeRaw || null,
    end_time: row.end_time || row.endTimeRaw || null,
    is_public_booking: !!(row.is_public_booking || row.is_public)
  }
  multiSelectState.cartSlots = [...multiSelectState.cartSlots, entry]
  persist(multiSelectState)
  return true
}

// Remove a slot from the cart by id. Used by CartLine's per-row remove
// affordance (and eventually by submit success).
export function removeSlotFromCart(id) {
  if (id == null) return
  multiSelectState.cartSlots = multiSelectState.cartSlots.filter(s => s.id !== id)
  persist(multiSelectState)
}

// Slot eligibility for cart mode. Mirrors canAppendToSlot's logic: we want
// OPEN slots only. Private slots that already have a transaction or are
// completed aren't candidates for a new transaction's cart. Public slots
// stay eligible (they can host multiple groups). Returns true when not in
// cart mode so non-cart click flows aren't accidentally blocked.
export function canSelectForCart(slot) {
  if (!multiSelectState.isActive) return true
  if (!slot) return true
  const row = slot.originalData || slot
  const isPublic = !!(row.is_public_booking || row.is_public)
  if (isPublic) return true
  const slotIsTaken = !!row.transaction_id || row.status === 'completed'
  return !slotIsTaken
}
